'use client';

// The Journal's "Desk recommendations": every pick and plan card the desk showed this user, as
// shown (levels, size, the evidence quoted, the session the figures were from), and what followed.
// A pick by the breakout rule is followed by the rule from the next open; a placed plan plays out
// in Paper trades.
import { Fragment, useEffect, useState } from 'react';
import { NSE } from '@planb/core';
import { api } from '@/lib/api';
import { useFeatures } from '@/lib/client/use-market';
import { cn } from '@/lib/utils';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { RowCard } from './row-card';

interface Outcome { followed: boolean; note?: string; entryDate?: string; entryPrice?: number; exitDate?: string; exitPrice?: number; open?: boolean; returnPct: number; r: number }
interface Rec {
  id: string; createdAt: string; source: 'scan_pick' | 'plan_card'; symbol: string; setup: string; rule?: string; group?: string;
  sessionDate: string; entry: number; stop: number; target?: number; qty: number; placed?: boolean; paperTradeId?: string; evidence: string;
  detail?: { line?: string; headline?: string }; outcome: Outcome;
}

const px = NSE.formatPrice;
const pct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
const shown = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });

function what(r: Rec) {
  if (r.source === 'scan_pick') return 'Desk pick';
  return r.placed ? 'Plan card · placed' : r.placed === false ? 'Plan card · not placed' : 'Plan card';
}

function Result({ o }: { o: Outcome }) {
  if (!o.followed || o.entryPrice == null) return <span className="text-muted-foreground">{o.note ?? '—'}</span>;
  return (
    <span className="num">
      <b className={cn(o.returnPct >= 0 ? 'text-bull' : 'text-bear')}>{pct(o.returnPct)}</b>
      {o.r !== 0 && <span className="text-muted-foreground"> · {o.r >= 0 ? '+' : ''}{o.r.toFixed(2)}R</span>}
      <span className="block text-[11px] text-muted-foreground">{o.open ? `open, at ₹${px(o.exitPrice ?? 0)} (${o.exitDate})` : `exited ${o.exitDate} at ₹${px(o.exitPrice ?? 0)}`} · in ₹{px(o.entryPrice)} on {o.entryDate}</span>
    </span>
  );
}

/** Shown only while its release switch is on for the user. */
export function DeskRecs() {
  const feat = useFeatures();
  return feat.on('rec_journal') ? <DeskRecsSection /> : null;
}

function DeskRecsSection() {
  const [rows, setRows] = useState<Rec[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    void api('/v1/recommendations').then((r) => (r.ok ? r.json() : null)).then((j) => setRows(j?.recommendations ?? [])).catch(() => setRows([]));
  }, []);
  if (!rows || rows.length === 0) return null;
  const followed = rows.filter((r) => r.outcome.followed && r.outcome.entryPrice != null);
  const evidence = (r: Rec) => (
    <div className="space-y-1 text-[12px] text-muted-foreground">
      {r.detail?.line && <p>{r.detail.line}</p>}
      {r.evidence && <p>{r.evidence}</p>}
      <p>Figures from the {r.sessionDate} session. Shown {shown(r.createdAt)}. Outcomes are after MoneyHQ’s 0.35% cost estimate, entered at the next open as the rule does; hypothetical, not advice.</p>
    </div>
  );
  return (
    <Card className="mt-6 gap-0 py-0">
      <CardHeader className="border-b py-4">
        <CardTitle className="text-[15px] font-semibold">Desk recommendations <span className="font-normal text-muted-foreground">· {rows.length}</span></CardTitle>
        <p className="mt-0.5 text-xs text-muted-foreground">Every pick and plan the desk showed you, as shown, and what happened after. Picks by the breakout rule are followed by the rule from the next open{followed.length ? ` (${followed.length} followed so far)` : ''}; placed plans play out in Paper trades.</p>
      </CardHeader>
      <div className="grid gap-2 p-3 sm:hidden">
        {rows.map((r) => (
          <div key={r.id} onClick={() => setOpen(open === r.id ? null : r.id)}>
            <RowCard
              title={<>{r.group && <span className="mr-1.5 text-[11px] font-semibold text-bull">{r.group}</span>}{r.symbol}</>}
              sub={<>{what(r)} · {shown(r.createdAt)}</>}
              cells={[
                { label: 'Shown at', value: `₹${px(r.entry)}` },
                { label: 'Stop', value: `₹${px(r.stop)}` },
                { label: 'Qty', value: String(r.qty || '—') },
              ]}
              actions={<Result o={r.outcome} />} />
            {open === r.id && <div className="px-1 pt-1.5">{evidence(r)}</div>}
          </div>
        ))}
      </div>
      <div className="overflow-x-auto max-sm:hidden">
        <table className="w-full min-w-[820px] text-[13px]">
          <thead className="text-left text-[11px] tracking-wide text-muted-foreground uppercase">
            <tr className="border-b">{['Shown', 'Stock', 'What', 'Price shown', 'Stop', 'Qty', 'Since then'].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr className="cursor-pointer border-b border-border/60 hover:bg-muted/40" onClick={() => setOpen(open === r.id ? null : r.id)} title="Show the evidence as shown">
                  <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{shown(r.createdAt)}</td>
                  <td className="px-3 py-2 font-semibold">{r.group && <span className="mr-1.5 text-[11px] text-bull">{r.group}</span>}{r.symbol}</td>
                  <td className="px-3 py-2">{what(r)}<div className="text-[11px] text-muted-foreground">{r.setup}</div></td>
                  <td className="num px-3 py-2">₹{px(r.entry)}</td>
                  <td className="num px-3 py-2">₹{px(r.stop)}</td>
                  <td className="num px-3 py-2">{r.qty || '—'}</td>
                  <td className="px-3 py-2"><Result o={r.outcome} /></td>
                </tr>
                {open === r.id && <tr className="border-b border-border/60"><td colSpan={7} className="px-3 py-2">{evidence(r)}</td></tr>}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
